import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  forwardRef,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DatepickerComponent as LibDatepicker } from 'nebula-ui-kit';
import { catchError, of, tap } from 'rxjs';
import {
  AlertStore,
  TranslationService,
  DateRangePickerServices,
  Module,
} from '@nebula-shared/data-access';

/**
 * Envoltorio de un único {@code lib-datepicker} que actúa como
 * {@link ControlValueAccessor}.
 *
 * <p>Espejo de {@code DateRangePickerComponent} para una sola fecha.
 * Replica su estructura —{@code signal} + {@code output} + {@code effect}
 * sobre {@code systemDate} + guard {@code esFecha} + anuncio del valor
 * de partida UNA vez— con un único valor en lugar de un rango.</p>
 *
 * <p>Al implementar CVA, el padre puede usarlo con {@code [formControl]}
 * o {@code formControlName} sin necesidad de pasar el control como input.
 * El wrapper escribe en el control vía {@code onChange} cuando el
 * datepicker cambia, y lee de él vía {@code writeValue} cuando el padre
 * lo actualiza (precargas, cancelar, cierre exitoso).</p>
 *
 * <p>El guard {@link esFecha} evita el bug clásico: la fecha del sistema
 * llega de una consulta que puede responder VACÍA sin dar error; un
 * {@code Date} con {@code NaN} como {@code maxDate} bloquearía el
 * datepicker sin que el usuario entienda por qué.</p>
 */
@Component({
  selector: 'app-single-datepicker',
  standalone: true,
  imports: [LibDatepicker],
  templateUrl: './single-datepicker.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SingleDatepickerComponent),
      multi: true,
    },
  ],
})
export class SingleDatepickerComponent extends AlertStore implements OnInit, ControlValueAccessor {
  protected service = inject(DateRangePickerServices);
  protected translation = inject(TranslationService);

  protected t(key: string): string {
    return this.translation.translate(key);
  }

  // ─── Inputs de presentación ─────────────────────────────
  required = input<boolean>(false);
  disabled = input<boolean>(false);
  placeholder = input<string>('dd/MM/yyyy');
  errorMsg = input<string | null>(null);
  label = input<string>('');
  tooltip = input<string>('');

  // ─── Inputs de fecha ────────────────────────────────────
  minDate = input<Date | null>(null);
  /**
   * Tope superior. Acepta {@code undefined} para alinearse con el
   * {@code computed<Date | undefined>} del padre sin fricción.
   */
  maxDate = input<Date | undefined>(undefined);
  module = input<Module>('accounting');

  // ─── Output ─────────────────────────────────────────────
  /**
   * Se emite cada vez que el datepicker cambia de fecha y también UNA
   * vez con el valor inicial (si el control ya traía una fecha válida).
   */
  dateChange = output<Date | null>();

  // ─── Estado interno ─────────────────────────────────────
  systemDate = signal<Date | null>(null);

  /** Valor espejo que pinta el datepicker crudo. */
  readonly valorInterno = signal<Date | null>(null);

  /** Estado disabled que Angular comunica vía setDisabledState. */
  private readonly disabledInterno = signal(false);

  /** Combina el disabled del CVA con el del input. */
  readonly isDisabled = computed(() => this.disabledInterno() || this.disabled());

  /**
   * Tope efectivo del datepicker.
   *
   * <p>Cascada reactiva: primero el valor del padre (que ya es un
   * {@code computed} en {@code BankClosuresComponent}) y, si no, la
   * fecha del sistema. Sin fallback a {@code new Date()} para no
   * bloquear el selector mientras carga el contexto.</p>
   */
  readonly maxDateModel = computed<Date | undefined>(() => {
    const delPadre = this.maxDate();
    if (SingleDatepickerComponent.esFecha(delPadre)) return delPadre;

    const delSistema = this.systemDate();
    if (SingleDatepickerComponent.esFecha(delSistema)) return delSistema;

    return undefined;
  });

  // ─── CVA: callbacks ─────────────────────────────────────
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onChange: (value: Date | null) => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onTouched: () => void = () => {};

  /** El valor de partida se anuncia una sola vez. */
  private valorDePartidaAnunciado = false;

  constructor() {
    super();

    effect(() => {
      const systemDateNow = this.systemDate();
      if (systemDateNow !== null) {
        this.anunciarValorDePartida();
      }
    });
  }

  ngOnInit(): void {
    this.getSystemParams();
  }

  // ─── ControlValueAccessor ───────────────────────────────
  /**
   * Angular llama a este método cuando el control del padre cambia por
   * fuera (precargas, cancelar, cierre exitoso). Refleja el valor en la
   * signal interna que pinta el datepicker.
   */
  writeValue(value: Date | null): void {
    this.valorInterno.set(value ?? null);
  }

  /** Angular registra aquí el callback para propagar cambios al control. */
  registerOnChange(fn: (value: Date | null) => void): void {
    this.onChange = fn;
  }

  /** Angular registra aquí el callback para marcar el control como touched. */
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  /** Angular comunica cambios de estado disabled del control. */
  setDisabledState(isDisabled: boolean): void {
    this.disabledInterno.set(isDisabled);
  }

  // ─── Handler del datepicker crudo ───────────────────────
  /**
   * El datepicker crudo emitió un cambio. El wrapper:
   * <ol>
   *   <li>Actualiza {@link valorInterno} (fuente de verdad del
   *       datepicker crudo).</li>
   *   <li>Propaga el valor al control del padre vía {@code onChange}.</li>
   *   <li>Marca el control como tocado vía {@code onTouched}.</li>
   *   <li>Reemite hacia arriba por {@link dateChange} para que el padre
   *       pueda aplicar lógica extra.</li>
   * </ol>
   */
  onDateChange(date: Date | null): void {
    this.valorInterno.set(date);
    this.onChange(date);
    this.onTouched();
    this.dateChange.emit(date);
  }

  // ─── Anuncio del valor de partida ───────────────────────
  /**
   * Emite el valor inicial que traía el control, UNA sola vez.
   *
   * <p>Sin esto, una pantalla que solo escucha {@code dateChange} se
   * queda sin saber que el selector ya arrancó con una fecha (por
   * ejemplo, la que el backend persistió en el último cierre). El
   * control sí la tiene, pero el evento no se emite porque el usuario
   * no ha tocado el control.</p>
   */
  private anunciarValorDePartida(): void {
    if (this.valorDePartidaAnunciado) return;

    const fecha = this.valorInterno();
    if (!SingleDatepickerComponent.esFecha(fecha)) return;

    this.valorDePartidaAnunciado = true;
    this.dateChange.emit(fecha);
  }

  /**
   * Dice si el valor es una fecha con la que se puede comparar.
   *
   * <p>Mismo guard que {@code DateRangePickerComponent}: la fecha del
   * sistema llega de una consulta que puede responder VACÍA sin dar
   * error; usarla como tope bloquearía el datepicker por completo.</p>
   */
  private static esFecha(fecha: Date | null | undefined): fecha is Date {
    return fecha instanceof Date && !Number.isNaN(fecha.getTime());
  }

  // ─── Carga de la fecha del sistema ──────────────────────
  getSystemParams(): void {
    this.service
      .getSystemParams(this.module())
      .pipe(
        tap((response) => {
          this.systemDate.set(new Date(response.fechaSistema));
        }),
        catchError((error) => {
          this.setError(error?.error.message || 'No se pudo cargar fecha del sistema');
          this.systemDate.set(null);
          return of(null);
        }),
      )
      .subscribe();
  }
}
